import type { DataSourceInput, FileRef } from '@/types/dataSources';

/**
 * Utility functions for handling file_id format conversion.
 *
 * The file_id format (e.g., "file_id:123") is used to reference user-uploaded
 * files by their database ID instead of expiring signed URLs. This enables:
 * - Session-wide document availability for follow-up queries
 * - Fresh signed URL generation at processing time
 * - No URL expiration issues (database IDs never expire)
 */

/**
 * Type for uploaded file with optional database ID.
 */
export interface UploadedFile {
  name: string;
  url: string;
  fileId?: number;
  dataSource?: DataSourceInput;
  /**
   * Pre-computed data source key for the API (e.g. "file_id:194" or "storage_node:194").
   * Encodes which storage system the file belongs to. Prefer this over rebuilding from fileId.
   */
  dataSrc?: string;
}

/**
 * Convert an array of uploaded files to data_sources format for the API.
 *
 * Uses the pre-computed `dataSrc` field when available (e.g. "file_id:194" for
 * legacy user_files uploads, "storage_node:194" for new storage system uploads).
 * Falls back to rebuilding from fileId (legacy behaviour), then to the raw URL.
 *
 * @param files - Array of uploaded file objects
 * @returns Array of data source inputs for the API
 *
 * @example
 * // With dataSrc (preferred — set by useSupabaseUpload)
 * convertToDataSources([{ name: 'doc.pdf', url: '...', fileId: 123, dataSrc: 'file_id:123' }])
 * // Returns: [{ kind: 'user_file', id: 123 }]
 *
 * @example
 * // Without dataSrc (backwards compatibility)
 * convertToDataSources([{ name: 'doc.pdf', url: 'https://...' }])
 * // Returns: ['https://...']
 */
export function convertToDataSources(files: UploadedFile[]): DataSourceInput[] {
  return files.map((file) => {
    if (file.dataSource) {
      return file.dataSource;
    }
    if (file.dataSrc?.startsWith('file_id:')) {
      const id = parseFileId(file.dataSrc);
      if (id !== null) {
        return { kind: 'user_file', id };
      }
    }
    if (file.dataSrc?.startsWith('storage_node:')) {
      const idPart = file.dataSrc.split(':', 2)[1];
      if (idPart) {
        const parsed = Number.parseInt(idPart, 10);
        if (!Number.isNaN(parsed)) {
          return { kind: 'storage_node', id: parsed };
        }
      }
    }
    if (file.fileId !== undefined && file.fileId !== null) {
      return { kind: 'user_file', id: file.fileId };
    }
    return file.url;
  });
}

export function normalizeDataSourcesFromApi(
  dataSources: Array<string | DataSourceInput> | null | undefined
): DataSourceInput[] {
  if (!dataSources) return [];
  return dataSources.map((dataSource) => {
    if (typeof dataSource !== 'string') {
      return dataSource;
    }
    if (dataSource.startsWith('file_id:')) {
      const id = parseFileId(dataSource);
      return id !== null
        ? ({ kind: 'user_file', id } satisfies FileRef)
        : dataSource;
    }
    if (dataSource.startsWith('storage_node:')) {
      const idPart = dataSource.split(':', 2)[1];
      if (!idPart) return dataSource;
      const parsed = Number.parseInt(idPart, 10);
      return Number.isNaN(parsed)
        ? dataSource
        : ({ kind: 'storage_node', id: parsed } satisfies FileRef);
    }
    return dataSource;
  });
}

/**
 * Check if a data source string is in file_id format.
 *
 * @param dataSource - The data source string to check
 * @returns True if the string starts with "file_id:"
 *
 * @example
 * isFileIdFormat('file_id:123') // true
 * isFileIdFormat('https://example.com/file.pdf') // false
 */
export function isFileIdFormat(dataSource: string): boolean {
  return dataSource.startsWith('file_id:');
}

/**
 * Extract the numeric file ID from a file_id format string.
 *
 * @param dataSource - The data source string in "file_id:123" format
 * @returns The numeric file ID, or null if parsing fails
 *
 * @example
 * parseFileId('file_id:123') // 123
 * parseFileId('file_id:not_a_number') // null
 * parseFileId('https://example.com') // null
 */
export function parseFileId(dataSource: string): number | null {
  if (!isFileIdFormat(dataSource)) {
    return null;
  }

  const idPart = dataSource.split(':', 2)[1];
  if (!idPart) {
    return null;
  }

  const parsed = Number.parseInt(idPart, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * Create a file_id format string from a numeric ID.
 *
 * @param fileId - The numeric file ID
 * @returns The formatted string "file_id:{id}"
 *
 * @example
 * createFileIdString(123) // 'file_id:123'
 */
export function createFileIdString(fileId: number): string {
  return `file_id:${fileId}`;
}
